import { z } from 'zod';

import { StatsWindowSchema } from '../../stats/stats-range/stats-range';

/** A money figure compared against the previous window — `ComparableCount`'s non-integer twin. */
export const ComparableAmountSchema = z
  .object({
    current: z.number(),
    previous: z.number(),
  })
  .strict();

export type ComparableAmount = z.infer<typeof ComparableAmountSchema>;

/** Confirmed revenue in one ISO 4217 currency — direct orders quote in several. */
export const CurrencyAmountSchema = z
  .object({
    currency: z.string().length(3),
    amount: z.number(),
  })
  .strict();

export type CurrencyAmount = z.infer<typeof CurrencyAmountSchema>;

/** One bucket of the revenue chart, in the platform's own `billing.currency`. */
export const RevenuePointSchema = z
  .object({
    bucket: z.string(),
    amount: z.number(),
  })
  .strict();

export type RevenuePoint = z.infer<typeof RevenuePointSchema>;

/** One bucket of the new-entitlements chart, split by store. */
export const EntitlementStorePointSchema = z
  .object({
    bucket: z.string(),
    appStore: z.number().int().nonnegative(),
    playStore: z.number().int().nonnegative(),
    direct: z.number().int().nonnegative(),
    promo: z.number().int().nonnegative(),
  })
  .strict();

export type EntitlementStorePoint = z.infer<typeof EntitlementStorePointSchema>;

/** Active entitlements by store — a `BarList` breakdown. */
export const EntitlementStoreBreakdownSchema = z
  .object({
    appStore: z.number().int().nonnegative(),
    playStore: z.number().int().nonnegative(),
    direct: z.number().int().nonnegative(),
    promo: z.number().int().nonnegative(),
  })
  .strict();

export type EntitlementStoreBreakdown = z.infer<typeof EntitlementStoreBreakdownSchema>;

/**
 * The Dashboard's Billing section (D12). `revenue` is in `billing.currency` —
 * the platform's own reporting currency — with any direct order settled in a
 * different currency listed separately in `revenueByOtherCurrency` rather than
 * silently summed into a figure that would mean nothing.
 */
export const BillingStatsSchema = z
  .object({
    window: StatsWindowSchema,
    currency: z.string().length(3),
    revenue: ComparableAmountSchema,
    revenueByOtherCurrency: z.array(CurrencyAmountSchema),
    activeEntitlements: z.number().int().nonnegative(),
    newEntitlements: z.number().int().nonnegative(),
    refundsRecorded: z.number().int().nonnegative(),
    rejectedReceipts: z.number().int().nonnegative(),
    feePaymentsSettled: z.number().int().nonnegative(),
    activeEntitlementsByStore: EntitlementStoreBreakdownSchema,
    revenuePerBucket: z.array(RevenuePointSchema),
    newEntitlementsPerBucket: z.array(EntitlementStorePointSchema),
  })
  .strict();

export type BillingStats = z.infer<typeof BillingStatsSchema>;
